import axios from "axios";
import router from "@/router/index";
import { deleteCookie } from "@/functions/cookies.js";
import { clearArchitectToken, getArchitectToken } from "@/functions/authToken.js";
import { useAuthStore } from "@/store/auth";
import { useApplicationStore } from "@/store/application";

const skipResponseUrls = ["files"];
const BACKEND_ERROR_TRANSLATIONS = Object.freeze({
  "Selected exam does not have result for the given user.":
    "Wybrany egzamin nie ma wyniku dla wskazanego uzytkownika.",
  "Selected model does not have accredible uid configured.":
    "Wybrany model nie ma skonfigurowanego accredible uid.",
  "Certificate generation failed.": "Generowanie certyfikatu nie powiodlo sie.",
  "The given data was invalid.": "Przekazane dane sa niepoprawne.",
  "The selected type is invalid.": "Wybrany typ jest niepoprawny.",
});

function skipResponse(response) {
  const prefix = response.config.url.split("/")[0];
  return skipResponseUrls.findIndex((url) => url === prefix) !== -1;
}

function translateBackendErrorMessage(message) {
  if (typeof message !== "string") {
    return message;
  }
  return BACKEND_ERROR_TRANSLATIONS[message] || message;
}

function translateBackendValidationErrors(errors) {
  if (!errors || typeof errors !== "object") {
    return;
  }

  Object.keys(errors).forEach((key) => {
    const value = errors[key];
    if (typeof value === "string") {
      errors[key] = translateBackendErrorMessage(value);
      return;
    }

    if (Array.isArray(value)) {
      errors[key] = value.map((item) =>
        typeof item === "string" ? translateBackendErrorMessage(item) : item
      );
      return;
    }

    if (typeof value === "object") {
      translateBackendValidationErrors(value);
    }
  });
}

axios.defaults.withCredentials = true;

axios.defaults.headers = {
  Accept: "application/json",
  Source: "web",
};

axios.interceptors.request.use(
  (config) => {
    const token = getArchitectToken();
    config.headers = config.headers || {};
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    const { addToast } = useApplicationStore();
    console.log(response.config.url);
    if (
      response.config.method !== "get" &&
      response.config.url != "/login" &&
      response.config.url != "broadcasting/auth" &&
      !skipResponse(response) &&
      !response.config.skipToast
    ) {
      let details = "Pomyslnie zapisano";
      if (response.config.method == "delete") {
        details = "Pomyslnie usunieto";
      }
      addToast({
        severity: "success",
        summary: "Sukces",
        detail: details,
        life: 3000,
        microtime: Math.floor(new Date().getTime()),
      });
    }
    return response;
  },
  (error) => {
    console.log("ERROR IN AXIOS", error);
    const { addToast } = useApplicationStore();
    const responseStatus = error.response?.status;
    const responseData = error.response?.data;

    if (responseData?.message) {
      responseData.message = translateBackendErrorMessage(responseData.message);
    }
    if (responseData?.errors) {
      translateBackendValidationErrors(responseData.errors);
    }

    if (responseStatus === 401) {
      const { clearUser } = useAuthStore();
      deleteCookie("architect-logged");
      clearArchitectToken();
      clearUser();
      router.push("/login");
    }

    if (error.config?.skipErrorToast) {
      return Promise.reject(error);
    }

    if (responseStatus === 504) {
      addToast({
        severity: "error",
        summary: "Blad",
        detail:
          "Przekroczony limit czasu oczekiwania na odpowiedz serwera. Skontaktuj sie z administratorem.",
        life: 300000,
        microtime: Math.floor(new Date().getTime()),
      });
    } else {
      addToast({
        severity: "error",
        summary: "Blad",
        detail: responseData?.message || "Wystapil blad.",
        life: 3000,
        microtime: Math.floor(new Date().getTime()),
      });
    }

    return Promise.reject(error);
  }
);

if (process.env.NODE_ENV === "production") {
  axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}/api/architect`;
} else {
  axios.defaults.baseURL = `/api/architect`;
}

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    return Promise.reject(error);
  }
);

export { axios };
